import React, { useEffect, useMemo, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled, { css } from 'styled-components';

export const MaxSizeAspectRatioContainerContext = React.createContext<{
  height?: number;
  width?: number;
  scale?: number;
}>({
  height: 9,
  width: 16,
  scale: 1,
});

const MaxSizeAspectRatioContainer = ({
  children,
  style,
  aspect,
}: {
  children: React.ReactNode;
  aspect: {
    width: number;
    height: number;
  };
  style?: React.CSSProperties;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerFieldRef = useRef<HTMLDivElement>(null);
  const maxContainerRef = useRef<HTMLDivElement>(null);

  const [wh, setWH] = useState<{
    w: number;
    h: number;
    widthCSS?: string;
    heightCSS?: string;
  }>();
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const calculateAndSetMaxWidth = () => {
      const aspectRatio = aspect.width / aspect.height;
      const maxHeight = maxContainerRef.current?.clientHeight || 0;
      const maxWidth = maxContainerRef.current?.clientWidth || 0;

      if (maxWidth / aspectRatio >= maxHeight) {
        // We fill height to 100% of the container
        setWH({
          w: maxHeight * aspectRatio,
          h: maxHeight,
          widthCSS: undefined,
          heightCSS: undefined,
        });
      } else {
        // We fill width to 100% of the container
        setWH({
          w: maxWidth,
          h: maxWidth / aspectRatio,
          widthCSS: '100%',
          heightCSS: undefined,
        });
      }
    };

    const calculateAndSetScale = () => {
      const scaleContainerWidth = containerRef.current?.clientWidth || 0;
      const scale = scaleContainerWidth / aspect.width;
      setScale(scale);
    };

    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        calculateAndSetMaxWidth();
        calculateAndSetScale();
      });
    });
    ro.observe(containerFieldRef.current as HTMLElement);

    // Upon first render
    calculateAndSetMaxWidth();
    calculateAndSetScale();

    return () => {
      ro.disconnect();
    };
  }, [aspect]);

  const contextValue = useMemo<{ height?: number; width?: number; scale?: number }>(
    () => ({ height: wh?.h || 0 * scale, width: wh?.w || 0 * scale, scale }),
    [wh, scale],
  );

  return (
    <MaxSizeContainer ref={maxContainerRef}>
      <FieldContainer style={style ? style : {}} ref={containerFieldRef}>
        <RefContainer2
          ref={containerRef}
          $aspectRatio={aspect.width / aspect.height}
          $width={wh?.w || 0}
          $widthCSS={wh?.widthCSS}
        >
          {/* Fixed height that sets the actual size of the slide */}
          <FixedHeightContainer
            $height={aspect.height * scale}
            $width={aspect.width * scale}
          >
            {/* The Scaler that transforms the FixedHeightContainer to fit with the avaible viewbox */}
            <ScaleContainer $height={aspect.height} $width={aspect.width} $scale={scale}>
              <MaxSizeAspectRatioContainerContext.Provider value={contextValue}>
                {children}
              </MaxSizeAspectRatioContainerContext.Provider>
            </ScaleContainer>
          </FixedHeightContainer>
        </RefContainer2>
      </FieldContainer>
    </MaxSizeContainer>
  );
};

export default MaxSizeAspectRatioContainer;

const MaxSizeContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const FixedHeightContainer = styled.div<{ $width: number; $height: number }>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  position: absolute;
  top: 0;
  left: 0;
`;

const RefContainer2 = styled.div<{
  $width: number;
  $aspectRatio: number;
  $widthCSS?: string;
}>`
  position: relative;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  width: ${({ $width, $widthCSS }) => ($widthCSS ? $widthCSS : `${$width}px`)};
  max-width: 100%;
`;

const FieldContainer = styled.div<{
  $maxWidth?: number;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  // Change the flex center css to scale horisontally or vertically
  ${({ $maxWidth }) => css`
    flex-direction: ${$maxWidth ? 'column' : 'row'};
  `}
`;

const ScaleContainer = styled.div<{
  $scale: number;
  $width: number;
  $height: number;
}>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transform: scale(${({ $scale }) => $scale});

  position: absolute;
  transform-origin: top left;
  top: 0;
  left: 0;
`;
